import { CompoundEntityRef } from '@backstage/catalog-model';
import { FetchApi } from '@backstage/core-plugin-api';
import { TechDocsStorageApi } from '@backstage/plugin-techdocs-react';

import { VersionsManifest, parseVersionsManifest } from './versioning';

/**
 * Session-scoped cache of parsed `versions.json` manifests, keyed per entity
 * and tier scope. The `VersionPicker`/`TierSelector` addons and the
 * `TechDocsReaderRedirect` wrapper read through this so each manifest is
 * fetched at most once per session (Task 3: "cached per entity for the
 * session"). Failures resolve to `null` and are cached so the picker/redirect
 * fail closed without retry storms (AC 10).
 *
 * Story 6.3: manifests are per tier under the tier layout (ADR T2 —
 * `<entity>/<tier>/versions.json`). Pass `tier` to read a tier's manifest;
 * omit it for the entity-root manifest (legacy pre-tier layout).
 */
const cache = new Map<string, Promise<VersionsManifest | null>>();

function entityKey(ref: CompoundEntityRef): string {
  return `${ref.kind}:${ref.namespace}:${ref.name}`.toLowerCase();
}

export function fetchVersionsManifest(
  storageApi: TechDocsStorageApi,
  fetchApi: FetchApi,
  entityRef: CompoundEntityRef,
  tier?: string,
): Promise<VersionsManifest | null> {
  const key = `${entityKey(entityRef)}#${tier ?? ''}`;
  let pending = cache.get(key);
  if (!pending) {
    // Do NOT use storageApi.getEntityDocs here: it is built for rendered HTML
    // pages and unconditionally appends `/index.html`, so it would request
    // `versions.json/index.html` and 404. Resolve the raw static-asset URL via
    // getBaseUrl and fetch the manifest file directly with the Backstage
    // fetchApi (which attaches the auth token the static route requires).
    const manifestPath = tier ? `${tier}/versions.json` : 'versions.json';
    pending = storageApi
      .getBaseUrl(manifestPath, entityRef, '')
      .then(url => fetchApi.fetch(url))
      .then(res => (res.ok ? res.text() : null))
      .then(raw => (raw === null ? null : parseVersionsManifest(raw)))
      .catch(() => null);
    cache.set(key, pending);
  }
  return pending;
}

/** Test-only: clear the session cache between cases. */
export function __clearVersionsCache(): void {
  cache.clear();
}
